package com.arena_web.utils

import kotlinx.browser.window

/**
 * Meta (Facebook) Pixel conversion-event helper.
 *
 * The pixel BASE code — the fbevents.js loader, `fbq('init')`, the initial
 * `PageView`, and the SPA history-navigation PageView hook — lives in the static
 * `<head>` (see `site/build.gradle.kts` head.add). It runs the instant the page
 * loads, before the Kotlin/JS bundle hydrates, so ad visitors who bounce during
 * load are still counted. That head snippet respects a prior explicit "declined"
 * and skips localhost.
 *
 * This object only fires CONVERSION events against the already-global `fbq`. The
 * head stub queues calls until fbevents.js finishes loading, so events fired
 * early are not lost. When the pixel was suppressed (declined), `window.fbq` is
 * undefined and every method is a safe no-op.
 */
object MetaPixel {
    /** Must match the id in the `<head>` base snippet (site/build.gradle.kts). */
    private const val PIXEL_ID = "1794405318410770"

    /**
     * Enable Meta Advanced Matching for the signed-in user. Re-initialises the
     * pixel with the user's email + id so every subsequent client event
     * (PageView on SPA nav, InitiateCheckout, Subscribe…) carries hashed
     * identifiers — this is what lifts "Event Match Quality" above the
     * cookie-only floor. Meta hashes the raw values in-browser (SHA-256); we
     * never send plaintext to the network ourselves. Safe no-op if the pixel was
     * suppressed (declined) — `window.fbq` is then undefined.
     */
    fun setAdvancedMatching(email: String?, externalId: String?) {
        val fbq = window.asDynamic().fbq
        if (fbq == null) return
        if (email.isNullOrBlank() && externalId.isNullOrBlank()) return
        val userData = js("({})")
        email?.takeIf { it.isNotBlank() }?.let { userData.em = it }
        externalId?.takeIf { it.isNotBlank() }?.let { userData.external_id = it }
        fbq("init", PIXEL_ID, userData)
    }

    /**
     * The user started the subscription checkout (pressed "buy", about to be
     * redirected to the mono payment page). Standard `InitiateCheckout` event —
     * the mid-funnel signal between `PageView` and `Purchase` that lets Meta
     * optimise toward people likely to pay, not only those who already have.
     *
     * NB: the terminal `Purchase`/`Subscribe` conversion is fired server-side
     * (backend `MetaCapiService.sendPurchase` off the mono settlement) so it is
     * not lost to adblock/ITP; the client only owns this mid-funnel intent.
     */
    fun trackInitiateCheckout(value: Double, currency: String) {
        val fbq = window.asDynamic().fbq
        if (fbq == null) return
        val params = js("({})")
        params.value = value
        params.currency = currency
        fbq("track", "InitiateCheckout", params)
    }

    /**
     * The trial checkout redirect — Meta's standard StartTrial funnel event.
     * Like InitiateCheckout this is the client-side intent signal; the paid
     * conversion (Purchase on trial→paid) fires server-side.
     */
    fun trackStartTrial(value: Double, currency: String) {
        val fbq = window.asDynamic().fbq
        if (fbq == null) return
        val params = js("({})")
        params.value = value
        params.currency = currency
        fbq("track", "StartTrial", params)
    }

    /** Custom event: the user joined a sport event — secondary ad signal. */
    fun trackJoinEvent(eventId: String) {
        val fbq = window.asDynamic().fbq
        if (fbq == null) return
        val params = js("({})")
        params.event_id = eventId
        fbq("trackCustom", "JoinEvent", params)
    }

    /**
     * No-ops kept so existing [com.arena_web.ui.components.widgets.CookieConsent]
     * call sites stay valid. Init / PageView / SPA-nav tracking now live in the
     * static `<head>` snippet, which handles consent (declined) itself.
     */
    fun initIfConsented() {}
    fun onConsentGranted() {}
}
